import com.varabyte.kobweb.browser.api
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.dom.hasClass
import kotlinx.dom.removeClass
import org.jetbrains.compose.web.renderComposable
import org.w3c.dom.EventSource
import org.w3c.dom.EventSourceInit
import org.w3c.dom.MessageEvent
import org.w3c.dom.`get`
import org.gstechnovos.jexonplastech.pages.AboutPage as _org_gstechnovos_jexonplastech_pages_AboutPage
import org.gstechnovos.jexonplastech.pages.ContactPage as _org_gstechnovos_jexonplastech_pages_ContactPage
import org.gstechnovos.jexonplastech.pages.HomePage as _org_gstechnovos_jexonplastech_pages_HomePage
import org.gstechnovos.jexonplastech.pages.ProductDetailsPage as _org_gstechnovos_jexonplastech_pages_ProductDetailsPage
import org.gstechnovos.jexonplastech.pages.ProductsPage as _org_gstechnovos_jexonplastech_pages_ProductsPage

private fun forceReloadNow() {
    window.stop()
    window.location.reload()
}

private fun handleServerStatusEvents() {
    val status = document.getElementById("status")!!
    var lastVersion: Int? = null
    var shouldReload = false

    val warningIcon = status.children[0]!!
    val spinnerIcon = status.children[1]!!
    val statusText = status.children[2]!!

    status.addEventListener("transitionend", {
        if (status.hasClass("fade-out")) {
            status.removeClass("fade-out")
            if (shouldReload) {
                forceReloadNow()
            }
        }
    })

    val eventSource = EventSource("/api/kobweb-status", EventSourceInit(true))
    eventSource.addEventListener("version", { evt ->
        val version = (evt as MessageEvent).data.toString().toInt()
        if (lastVersion == null) {
            lastVersion = version
        }
        if (lastVersion != version) {
            lastVersion = version
            if (document.asDynamic().hidden) {
                // Reload immediately when the page is not visible as the animation will not run
                forceReloadNow()
            } else if (status.className.isNotEmpty()) {
                shouldReload = true
            } else {
                // Not sure if we can get here but if we can't rely on the status transition
                // to reload we should do it ourselves.
                forceReloadNow()
            }
        }
    })

    eventSource.addEventListener("status", { evt ->
        val values: dynamic = JSON.parse<Any>((evt as MessageEvent).data.toString())
        val text = values.text as String
        val isError = (values.isError as String).toBoolean()
        if (text.isNotBlank()) {
            warningIcon.className = if (isError) "visible" else "hidden"
            spinnerIcon.className = if (isError) "hidden" else "visible"
            statusText.innerHTML = "<i>$text</i>"
            status.className = "fade-in"
        } else {
            if (status.className == "fade-in") {
                status.className = "fade-out"
            }
        }
    })

    eventSource.onerror = { eventSource.close() }
}

public fun main() {
    handleServerStatusEvents()

    window.api.logOnError = true

    AppGlobals.initialize(mapOf("title" to "Jexon Plastech"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.register(
            "/",
        ) { pageCtx -> 
            _org_gstechnovos_jexonplastech_pages_HomePage()
        }
        ctx.router.register(
            "/about",
        ) { pageCtx -> 
            _org_gstechnovos_jexonplastech_pages_AboutPage()
        }
        ctx.router.register(
            "/contact",
        ) { pageCtx -> 
            _org_gstechnovos_jexonplastech_pages_ContactPage()
        }
        ctx.router.register(
            "/products",
        ) { pageCtx -> 
            _org_gstechnovos_jexonplastech_pages_ProductsPage()
        }
        ctx.router.register(
            "/products/{model}",
        ) { pageCtx -> 
            _org_gstechnovos_jexonplastech_pages_ProductDetailsPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("footer-link", org.gstechnovos.jexonplastech.sections.footer.FooterLinkStyle)
        ctx.theme.registerStyle("know-more-link", org.gstechnovos.jexonplastech.sections.footer.KnowMoreLinkStyle)
        ctx.theme.registerStyle("animated-footer-link", org.gstechnovos.jexonplastech.sections.footer.AnimatedFooterLinkStyle)
        ctx.theme.registerStyle("social-icon", org.gstechnovos.jexonplastech.sections.footer.SocialIconStyle)
        ctx.theme.registerStyle("capabilities-container", org.gstechnovos.jexonplastech.sections.about.styles.CapabilitiesContainerStyle)
        ctx.theme.registerStyle("capability-item", org.gstechnovos.jexonplastech.sections.about.styles.CapabilityItemStyle)
        ctx.theme.registerStyle("about-hero-container", org.gstechnovos.jexonplastech.sections.about.styles.AboutHeroContainerStyle)
        ctx.theme.registerStyle("executives-container", org.gstechnovos.jexonplastech.sections.about.styles.ExecutivesContainerStyle)
        ctx.theme.registerStyle("executive-card", org.gstechnovos.jexonplastech.sections.about.styles.ExecutiveCardStyle)
        ctx.theme.registerStyle("story-container", org.gstechnovos.jexonplastech.sections.about.styles.StoryContainerStyle)
        ctx.theme.registerStyle("timeline-item", org.gstechnovos.jexonplastech.sections.about.styles.TimelineItemStyle)
        ctx.theme.registerStyle("timeline-dot", org.gstechnovos.jexonplastech.sections.about.styles.TimelineDotStyle)
        ctx.theme.registerStyle("core-values-container", org.gstechnovos.jexonplastech.sections.about.styles.CoreValuesContainerStyle)
        ctx.theme.registerStyle("value-card", org.gstechnovos.jexonplastech.sections.about.styles.ValueCardStyle)
        ctx.theme.registerStyle("featured-categories-container", org.gstechnovos.jexonplastech.sections.home.styles.FeaturedCategoriesContainerStyle)
        ctx.theme.registerStyle("category-card", org.gstechnovos.jexonplastech.sections.home.styles.CategoryCardStyle)
        ctx.theme.registerStyle("category-title", org.gstechnovos.jexonplastech.sections.home.styles.CategoryTitleStyle)
        ctx.theme.registerStyle("primary-button", org.gstechnovos.jexonplastech.sections.home.styles.PrimaryButtonStyle)
        ctx.theme.registerStyle("secondary-button", org.gstechnovos.jexonplastech.sections.home.styles.SecondaryButtonStyle)
        ctx.theme.registerStyle("quality-standards-container", org.gstechnovos.jexonplastech.sections.home.styles.QualityStandardsContainerStyle)
        ctx.theme.registerStyle("quality-card", org.gstechnovos.jexonplastech.sections.home.styles.QualityCardStyle)
        ctx.theme.registerStyle("company-overview-container", org.gstechnovos.jexonplastech.sections.home.styles.CompanyOverviewContainerStyle)
        ctx.theme.registerStyle("stat-card", org.gstechnovos.jexonplastech.sections.home.styles.StatCardStyle)
        ctx.theme.registerStyle("contact-info-container", org.gstechnovos.jexonplastech.sections.contact.styles.ContactInfoContainerStyle)
        ctx.theme.registerStyle("contact-card", org.gstechnovos.jexonplastech.sections.contact.styles.ContactCardStyle)
        ctx.theme.registerStyle("contact-form-container", org.gstechnovos.jexonplastech.sections.contact.styles.ContactFormContainerStyle)
        ctx.theme.registerStyle("input", org.gstechnovos.jexonplastech.sections.contact.styles.InputStyle)
        ctx.theme.registerStyle("submit-button", org.gstechnovos.jexonplastech.sections.contact.styles.SubmitButtonStyle)
        ctx.theme.registerStyle("contact-header-container", org.gstechnovos.jexonplastech.sections.contact.styles.ContactHeaderContainerStyle)
        ctx.theme.registerStyle("animated-header-link", com.iregained.site.components.sections.header.AnimatedHeaderLinkStyle)
        ctx.theme.registerStyle("header-button", com.iregained.site.components.sections.header.HeaderButtonStyle)
        ctx.theme.registerStyle("hamburger-icon", com.iregained.site.components.sections.header.HamburgerIconStyle)
        ctx.theme.registerStyle("logo", com.iregained.site.components.sections.header.LogoStyle)
        ctx.theme.registerStyle("dropdown-item", com.iregained.site.components.sections.header.DropdownItemStyle)
        ctx.theme.registerStyle("header-link", com.iregained.site.components.sections.header.HeaderLinkStyle)
        ctx.theme.registerStyle("utility-link", com.iregained.site.components.sections.header.UtilityLinkStyle)
        ctx.theme.registerStyle("products-grid-container", org.gstechnovos.jexonplastech.sections.products.styles.ProductsGridContainerStyle)
        ctx.theme.registerStyle("product-card", org.gstechnovos.jexonplastech.sections.products.styles.ProductCardStyle)
        ctx.theme.registerStyle("filter-container", org.gstechnovos.jexonplastech.sections.products.styles.FilterContainerStyle)
        ctx.theme.registerStyle("filter-button", org.gstechnovos.jexonplastech.sections.products.styles.FilterButtonStyle)
        ctx.theme.registerStyle("search-input", org.gstechnovos.jexonplastech.sections.products.styles.SearchInputStyle)
        ctx.theme.registerStyle("products-header-container", org.gstechnovos.jexonplastech.sections.products.styles.ProductsHeaderContainerStyle)
        ctx.theme.registerStyle("headline-text", org.gstechnovos.jexonplastech.theme.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", org.gstechnovos.jexonplastech.theme.SubheadlineTextStyle)
        ctx.theme.registerStyle("yt-card-wrapper", com.iregained.site.components.widgets.styles.YtCardWrapperStyle)
        ctx.theme.registerStyle("yt-poster-image", com.iregained.site.components.widgets.styles.YtPosterImageStyle)
        ctx.theme.registerStyle("yt-poster-gradient", com.iregained.site.components.widgets.styles.YtPosterGradientStyle)
        ctx.theme.registerStyle("yt-play-button", com.iregained.site.components.widgets.styles.YtPlayButtonStyle)
        ctx.theme.registerStyle("yt-pulse-ring", com.iregained.site.components.widgets.styles.YtPulseRingStyle)
        ctx.theme.registerStyle("yt-label-pill", com.iregained.site.components.widgets.styles.YtLabelPillStyle)
        ctx.theme.registerStyle("yt-iframe", com.iregained.site.components.widgets.styles.YtIframeStyle)
        ctx.theme.registerStyle("shared-hero-image", com.iregained.site.components.widgets.styles.SharedHeroImageStyle)
        ctx.theme.registerStyle("global-cta-container", org.gstechnovos.jexonplastech.components.widgets.styles.GlobalCtaContainerStyle)
        ctx.theme.registerStyle("cta-button", org.gstechnovos.jexonplastech.components.widgets.styles.CtaButtonStyle)
        ctx.theme.registerVariant("-primary", org.gstechnovos.jexonplastech.theme.PrimaryButtonVariant
        )
        ctx.theme.registerKeyframes("header-curtain-drop", com.iregained.site.components.sections.header.HeaderCurtainDropKeyframes)
        ctx.theme.registerKeyframes("card-appear", org.gstechnovos.jexonplastech.sections.products.styles.CardAppearKeyframes)
        ctx.theme.registerKeyframes("hero-text-slide-right", org.gstechnovos.jexonplastech.components.widgets.HeroTextSlideRightKeyframes)
        ctx.theme.registerKeyframes("hero-media-scale-up", org.gstechnovos.jexonplastech.components.widgets.HeroMediaScaleUpKeyframes)
        ctx.theme.registerKeyframes("yt-pulse", com.iregained.site.components.widgets.styles.YtPulseAnimation)
        org.gstechnovos.jexonplastech.initColorMode(ctx)
        org.gstechnovos.jexonplastech.initStyles(ctx)
        org.gstechnovos.jexonplastech.theme.initTheme(ctx)
        org.gstechnovos.jexonplastech.theme.initSiteStyles(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        org.gstechnovos.jexonplastech.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
